import { useState, useEffect, FormEvent } from 'react';
import { useRouter } from 'next/router';
import { ShippingMethod } from '@Types/cart/ShippingMethod';
import { Cart as CartType } from '@Types/cart/Cart';
import { Organization } from '@Types/organization/organization';
import { useFormat } from 'helpers/hooks/useFormat';
import { Reference } from 'helpers/reference';
import { useAccount } from 'frontastic';
import { NextFrontasticImage } from 'frontastic/lib/image';
import Markdown from 'components/commercetools-ui/content/markdown';
import Spinner from 'components/commercetools-ui/spinner';
import EmptyCart from 'components/commercetools-ui/cart/emptyCart';
import ItemList from './itemList';
import OrderSummary from './orderSummary';
export interface Props {
  pageTitle?: string;
  emptyStateImage?: NextFrontasticImage;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  emptyStateCTALabel?: string;
  emptyStateCTALink?: Reference;
  cart: CartType;
  editItemQuantity: (lineItemId: string, newQuantity: number) => Promise<void>;
  removeItem: (lineItemId: string) => void;
  shippingMethods: ShippingMethod[];
  organization?: Organization;
  customText?: string;
  markdownText?: string;
}

const Cart = ({
  cart,
  editItemQuantity,
  removeItem,
  pageTitle,
  emptyStateImage,
  emptyStateTitle,
  emptyStateSubtitle,
  emptyStateCTALabel,
  emptyStateCTALink,
  organization,
  customText,
  markdownText,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isQuoteRequestDisabled, setIsQuoteRequestDisabled] = useState(true);
  const [isOverStock, setIsOverStock] = useState(false);
  const { account } = useAccount();

  //i18n messages
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const router = useRouter();

  const onCheckout = (e: FormEvent) => {
    e.preventDefault();
    router.push('/checkout');
  };

  const goToProductPage = (_url: string) => router.push(_url);

  useEffect(() => {
    if (cart?.lineItems) {
      setLoading(false);
    }
    setIsQuoteRequestDisabled(true);
    setIsOverStock(
      cart?.lineItems?.some((lineItem) => lineItem.count > lineItem.variant?.availability?.availableQuantity),
    );
  }, [cart]);

  if (loading) {
    return (
      <div className="flex items-stretch justify-center py-20 px-12">
        <Spinner />
      </div>
    );
  }

  if ((!loading && !cart?.lineItems) || cart.lineItems.length < 1) {
    return (
      <EmptyCart
        pageTitle={pageTitle}
        image={emptyStateImage}
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        ctaLabel={emptyStateCTALabel}
        ctaLink={emptyStateCTALink}
      />
    );
  }

  return (
    <main className="mx-auto pt-10">
      <h1 className="cart-title pb-12">
        {formatCartMessage({ id: 'cart.shopping', defaultMessage: 'Your Shopping Cart' })}
      </h1>
      <div className="custom-text">{customText}</div>
      <Markdown text={markdownText} />
      {loading ? (
        <div className="flex items-stretch justify-center py-10 px-12">
          <Spinner />
        </div>
      ) : (
        <form className="cart-wrapper">
          <ItemList
            cart={cart}
            editItemQuantity={editItemQuantity}
            goToProductPage={goToProductPage}
            removeItem={(lineItemId: string) => removeItem(lineItemId)}
            isModificationForbidden={!isQuoteRequestDisabled}
            className="mb-8 w-full"
          />
          <OrderSummary
            organization={organization}
            cart={cart}
            submitButtonLabel={cart.isPreBuyCart && 'Pre order'}
            disableSubmitButton={
              isOverStock ||
              !cart.customerId ||
              (cart.customerId === account?.accountId && !!organization?.superUserBusinessUnitKey)
            }
            onSubmit={onCheckout}
            showDiscountsForm={false}
            currentStep="cart"
            isQuoteRequestDisabled={isQuoteRequestDisabled}
          />
        </form>
      )}
    </main>
  );
};

export default Cart;
